/**
 * Pipeline comercial — oportunidades por estágio (Task 13, brief 3.4).
 *
 * Leia isto antes de mexer:
 *
 * 1. **A coluna não tem filtro próprio.** O recorte vem dos seletores de
 *    `crm/dataset.ts` (`pipelineColumns`, `opportunitiesByStage`), os MESMOS que
 *    a visão 360 usa — a contagem de uma coluna não pode discordar do que a
 *    tela do cliente mostra.
 * 2. **Soma de coluna sem valor declarado é dito, não escondido** (D-13.8):
 *    cada coluna informa quantas negociações estão sem valor, para "R$ 0,00"
 *    nunca ser lido como "nada em jogo".
 * 3. **Cada linha navega até o registro**: o cliente (visão 360), a conversa de
 *    origem e, quando a negociação foi ganha, o pedido que a concretizou.
 *
 * Reconstrução funcional (Fase 9, Plano de Ataque R8 — pedido nominal do
 * cliente: "Pipeline comercial deveria ser um CRM, não tem nada a ver com
 * isso"). Até aqui esta tela era um RELATÓRIO somente-leitura: mostrava
 * contagem e lista, mas não dava nenhuma ação. O backend (`packages/crm`) já
 * tinha tudo — máquina de estágios, prova de ganho/perda, trilha de eventos —
 * e existia até um componente de avanço de estágio pronto e testado
 * (`OpportunityStage`/`useOpportunityStage`), só que montado unicamente em
 * `Crm360.ts`. Esta fase NÃO adiciona regra nova: monta, aqui, o que já
 * existia em outro lugar.
 *
 * O que mudou de verdade:
 *  - a coluna "Negociação" da tabela agora é um LINK que seleciona a
 *    oportunidade (`?opportunity=`), preservando o recorte atual
 *    (estágio/dono/busca) — antes só um link externo (vindo do 360 ou de uma
 *    tarefa) conseguia selecionar algo aqui; agora a própria tela permite;
 *  - oportunidade selecionada abre um painel de detalhe com o MESMO
 *    `OpportunityStage` do 360 (histórico + formulário de avanço real,
 *    validado pela mesma regra pura do servidor) e os orçamentos/tarefas
 *    VINCULADOS a ela (`quotesOfOpportunity`/`tasksOfOpportunity`, seletores
 *    puros que já existiam e não tinham consumidor aqui);
 *  - a lista completa continua visível abaixo do painel (mesmo padrão da
 *    visão 360/Orçamentos/Tarefas: registro selecionado fica destacado na
 *    lista, nunca escondido do contexto).
 *
 * Redesign R6 (apresentação) + Fase 6 (microanimação): wrapper e header no
 * padrão aprovado; `animateRows` ligado nas três tabelas. Todos os `data-*`
 * (incl. `data-page`, `data-pipeline-summary`, `data-stage-count`,
 * `data-stage-without-value`, `data-selected-opportunity`), os textos e os
 * links PRESERVADOS — as classes hook seguem no markup.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide (`Filter`, o mesmo selo de funil
 * já usado em `OpportunityStage.tsx`) no título da tela, no mesmo padrão
 * visual de `KpiCard.tsx` (ícone + texto, nunca só cor) — esta tela não tinha
 * nenhum ícone antes. Nenhum `data-*`/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { useState, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Avatar, Segmented } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { CrmBoundary } from "../components/CrmBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { OpportunityStage } from "../components/OpportunityStage.js";
import {
  isCrmSourceAwaitingContract,
  opportunityTimeline,
  pipelineColumns,
  quotesOfOpportunity,
  tasksOfOpportunity,
  quoteView
} from "../crm/dataset.js";
import {
  ALLOWED_STAGE_TRANSITIONS,
  isLeadStage,
  LEAD_STAGE_LABELS,
  STAGE_TRANSITION_REQUIREMENTS,
  OPPORTUNITY_SOURCE_LABELS,
  QUOTE_STATUS_LABELS,
  TASK_KIND_LABELS,
  type LeadStage
} from "../crm/pipeline.js";
import { rankedCrmIds, searchCrm } from "../crm/search.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatDateTime } from "../format.js";
import { useOpportunityStage } from "../queries.js";
import type { CrmDataset, PipelineColumn } from "../crm/dataset.js";
import type { OpportunityStageHandle, PipelineViewProps } from "../crm/view-props.js";
import type { CrmOpportunity } from "../api/types.js";

/** Rótulo do status de tarefa — não existe mapa compartilhado (só 3 valores). */
const TASK_STATUS_LABELS: Record<string, string> = { open: "Aberta", done: "Concluída", cancelled: "Cancelada" };

/** Preserva o recorte atual (estágio/dono/busca) ao selecionar uma negociação. */
function opportunityHref(
  opportunityId: string,
  context: { stageFilter: LeadStage | null; ownerFilter: string | null; query: string | null }
): string {
  const params = new URLSearchParams();
  if (context.stageFilter) params.set("stage", context.stageFilter);
  if (context.ownerFilter) params.set("owner", context.ownerFilter);
  if (context.query) params.set("q", context.query);
  params.set("opportunity", opportunityId);
  return `/crm/pipeline?${params.toString()}`;
}

function OpportunityTable({
  opportunities,
  caption,
  selectedId = null,
  context
}: {
  opportunities: CrmOpportunity[];
  caption: string;
  selectedId?: string | null;
  context: { stageFilter: LeadStage | null; ownerFilter: string | null; query: string | null };
}): ReactElement {
  return (
    <RecordTable<CrmOpportunity>
      caption={caption}
      rows={opportunities}
      idOf={(opportunity) => opportunity.id}
      emptyMessage="Nenhuma oportunidade neste recorte."
      selectedId={selectedId}
      animateRows
      columns={[
        {
          key: "title",
          label: "Negociação",
          render: (opportunity) => (
            <Link
              to={opportunityHref(opportunity.id, context)}
              className="font-semibold text-brand-text hover:underline"
              aria-label={`Gerenciar negociação ${opportunity.title}`}
            >
              {opportunity.title}
            </Link>
          )
        },
        {
          key: "customer",
          label: "Cliente",
          render: (opportunity) => <Link to={`/clientes/${opportunity.customerId}`}>{opportunity.customerId}</Link>
        },
        {
          key: "stage",
          label: "Estágio",
          render: (opportunity) => <span data-opportunity-stage={opportunity.stage}>{LEAD_STAGE_LABELS[opportunity.stage]}</span>
        },
        {
          key: "owner",
          label: "Responsável",
          render: (opportunity) => <span data-opportunity-owner={opportunity.ownerProfileId}>{opportunity.ownerLabel}</span>
        },
        {
          key: "value",
          label: "Valor",
          render: (opportunity) =>
            opportunity.valueCents === null ? <span data-pending="true">sem valor declarado</span> : formatBrl(opportunity.valueCents)
        },
        {
          key: "source",
          label: "Origem",
          render: (opportunity) =>
            opportunity.conversationId ? (
              <Link to={`/inbox/${opportunity.conversationId}`}>{OPPORTUNITY_SOURCE_LABELS[opportunity.source]}</Link>
            ) : (
              OPPORTUNITY_SOURCE_LABELS[opportunity.source]
            )
        },
        { key: "updatedAt", label: "Atualizada em", render: (opportunity) => formatDateTime(opportunity.updatedAt) },
        {
          key: "history",
          label: "Histórico",
          render: (opportunity) => (
            <span data-opportunity-events={opportunityTimeline(opportunity).length}>
              {`${opportunityTimeline(opportunity).length} mudança(s) de estágio`}
            </span>
          )
        }
      ]}
    />
  );
}

// Classes alinhadas ao RecordTable migrado (cabeçalho em surface-soft, zebra,
// células densas) — o resumo do funil não é um RecordTable (tem `<th scope=row>`
// e colunas próprias), então replica o mesmo visual tokenizado à mão.
const SUMMARY_TH = "whitespace-nowrap px-3 py-2 text-xs font-bold uppercase tracking-wide text-muted text-left";
const SUMMARY_CELL = "whitespace-nowrap px-3 py-2 text-sm text-body align-top";

function ColumnSummary({ columns }: { columns: PipelineColumn[] }): ReactElement {
  return (
    <div
      className="overflow-x-auto rounded-panel border border-border bg-surface shadow-s1 focus-visible:outline-none"
      tabIndex={0}
      role="region"
      aria-label="Funil por estágio"
    >
      <table className="record-table w-full border-collapse text-left" data-pipeline-summary={columns.length}>
        <caption className="px-3 py-2 text-left text-sm text-muted">
          Funil por estágio: quantas negociações e quanto valor declarado em cada coluna
        </caption>
        <thead>
          <tr className="border-b border-border-strong bg-surface-soft">
            <th scope="col" className={SUMMARY_TH}>
              Estágio
            </th>
            <th scope="col" className={SUMMARY_TH}>
              Negociações
            </th>
            <th scope="col" className={SUMMARY_TH}>
              Valor declarado
            </th>
            <th scope="col" className={SUMMARY_TH}>
              Sem valor declarado
            </th>
          </tr>
        </thead>
        <tbody>
          {columns.map((column) => (
            <tr
              key={column.stage}
              data-record-id={column.stage}
              data-stage-count={column.opportunities.length}
              className="border-b border-border last:border-b-0 odd:bg-surface even:bg-surface-soft/40"
            >
              <th scope="row" className={`${SUMMARY_CELL} font-semibold`}>
                <Link to={`/crm/pipeline?stage=${encodeURIComponent(column.stage)}`} className="text-brand-text hover:underline">
                  {LEAD_STAGE_LABELS[column.stage]}
                </Link>
              </th>
              <td className={SUMMARY_CELL}>{String(column.opportunities.length)}</td>
              <td className={SUMMARY_CELL}>{formatBrl(column.valueCents)}</td>
              {/* Sem este número, cinco negociações sem valor virariam "R$ 0,00". */}
              <td className={SUMMARY_CELL} data-stage-without-value={column.withoutValue}>
                {String(column.withoutValue)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


/** Faixa de cor do topo de cada coluna (o estágio também vai por texto, nunca só cor). */
const STAGE_ACCENT: Record<LeadStage, string> = {
  novo: "border-t-border-strong",
  qualificando: "border-t-info",
  proposta: "border-t-warning",
  negociacao: "border-t-brand",
  ganho: "border-t-success",
  perdido: "border-t-danger"
};

/** Há quanto tempo a negociação não muda (só apresentação; vem de `updatedAt`). */
function idleLabel(iso: string, now: Date): string {
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(days) || days < 0) return "—";
  if (days === 0) return "hoje";
  return days === 1 ? "há 1 dia" : `há ${days} dias`;
}

/**
 * A mudança por arrastar só vale quando a transição é permitida E não exige prova
 * (`ganho` pede o pedido que fechou a venda; `perdido`, o motivo). Nesses casos, e
 * nas transições proibidas, o cartão NÃO se move: a tela abre a negociação para o
 * formulário de avanço real — o funil não aceita resultado sem a prova dele.
 */
export function dragMoveAllowed(from: LeadStage, to: LeadStage): boolean {
  return from !== to && ALLOWED_STAGE_TRANSITIONS[from].includes(to) && !(STAGE_TRANSITION_REQUIREMENTS[to]?.length);
}

function PipelineBoard({
  crm,
  columns,
  context,
  now,
  selectedId,
  onMove
}: {
  crm: CrmDataset;
  columns: PipelineColumn[];
  context: { stageFilter: LeadStage | null; ownerFilter: string | null; query: string | null };
  now: Date;
  selectedId: string | null;
  onMove?: (opportunityId: string, to: LeadStage) => void;
}): ReactElement {
  const [dragging, setDragging] = useState<CrmOpportunity | null>(null);
  const [over, setOver] = useState<LeadStage | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const names = new Map(crm.customers.map((customer) => [customer.id, customer.name]));

  function drop(to: LeadStage): void {
    const card = dragging;
    setDragging(null);
    setOver(null);
    if (!card || card.stage === to) return;
    if (dragMoveAllowed(card.stage, to) && onMove) {
      setHint(null);
      onMove(card.id, to);
      return;
    }
    const needs = STAGE_TRANSITION_REQUIREMENTS[to];
    setHint(
      ALLOWED_STAGE_TRANSITIONS[card.stage].includes(to)
        ? `Para mover "${card.title}" para ${LEAD_STAGE_LABELS[to]} é preciso informar ${needs?.length ? "a prova do resultado" : "os dados da transição"}: abra a negociação.`
        : `"${card.title}" não pode ir direto de ${LEAD_STAGE_LABELS[card.stage]} para ${LEAD_STAGE_LABELS[to]}.`
    );
  }

  return (
    <section aria-label="Quadro do funil" data-pipeline-board={columns.length}>
      {hint ? (
        <p role="status" className="mb-3 rounded-panel border border-warning bg-warning-soft px-4 py-3 text-sm font-bold text-ink" data-pipeline-hint="true">
          {hint}
        </p>
      ) : null}
      <div className="flex items-start gap-4 overflow-x-auto overflow-y-hidden pb-2">
        {columns.map((column) => (
          <div
            key={column.stage}
            data-board-stage={column.stage}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(column.stage);
            }}
            onDragLeave={() => setOver((current) => (current === column.stage ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              drop(column.stage);
            }}
            className={
              "flex w-64 shrink-0 flex-col gap-3 rounded-panel border border-t-4 border-border bg-surface-soft p-3 transition-colors " +
              STAGE_ACCENT[column.stage] +
              (over === column.stage ? " ring-2 ring-brand" : "")
            }
          >
            <header className="flex flex-col gap-0.5 px-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-extrabold text-ink">{LEAD_STAGE_LABELS[column.stage]}</h3>
                <span className="rounded-pill bg-surface px-2 py-0.5 text-xs font-extrabold tabular-nums text-body">{column.opportunities.length}</span>
              </div>
              <p className="m-0 text-xs font-bold tabular-nums text-muted">
                {formatBrl(column.valueCents)}
                {column.withoutValue > 0 ? ` · ${column.withoutValue} sem valor` : ""}
              </p>
            </header>
            {column.opportunities.length === 0 ? (
              <p className="m-0 rounded-control border border-dashed border-border-strong p-4 text-center text-xs text-muted">Solte um cartão aqui</p>
            ) : (
              column.opportunities.map((opportunity) => (
                <article
                  key={opportunity.id}
                  data-opportunity-card={opportunity.id}
                  aria-label={`${opportunity.title} — ${LEAD_STAGE_LABELS[opportunity.stage]}`}
                  draggable={opportunity.stage !== "ganho" && opportunity.stage !== "perdido"}
                  onDragStart={() => setDragging(opportunity)}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                  className={
                    "flex flex-col gap-2 rounded-panel border bg-surface p-3 shadow-s1 transition-shadow hover:shadow-s2 " +
                    (selectedId === opportunity.id ? "border-brand ring-1 ring-brand" : "border-border") +
                    (dragging?.id === opportunity.id ? " opacity-50" : "")
                  }
                >
                  <Link
                    to={opportunityHref(opportunity.id, context)}
                    className="text-sm font-extrabold leading-snug text-ink hover:underline"
                    aria-label={`Gerenciar negociação ${opportunity.title}`}
                  >
                    {opportunity.title}
                  </Link>
                  <Link to={`/clientes/${opportunity.customerId}`} className="truncate text-xs font-bold text-muted hover:text-ink">
                    {names.get(opportunity.customerId) ?? opportunity.customerId}
                  </Link>
                  <p className="m-0 text-base font-black tabular-nums text-ink">
                    {opportunity.valueCents === null ? <span className="text-sm font-bold text-muted" data-pending="true">sem valor declarado</span> : formatBrl(opportunity.valueCents)}
                  </p>
                  <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
                    <span className="flex min-w-0 items-center gap-2" data-opportunity-owner={opportunity.ownerProfileId}>
                      <Avatar name={opportunity.ownerLabel} />
                      <span className="truncate text-xs text-muted">{opportunity.ownerLabel}</span>
                    </span>
                    <span className="shrink-0 text-xs text-muted">{idleLabel(opportunity.updatedAt, now)}</span>
                  </div>
                </article>
              ))
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Painel de detalhe da negociação selecionada (Fase 9): o avanço de estágio
 * REAL (mesmo `OpportunityStage` do 360) + orçamentos e tarefas vinculados.
 * Nada aqui recalcula nada — `quotesOfOpportunity`/`tasksOfOpportunity` já
 * existiam em `packages/crm` sem nenhum consumidor; esta é a montagem.
 */
function OpportunityDetail({
  crm,
  opportunity,
  stage,
  now
}: {
  crm: CrmDataset;
  opportunity: CrmOpportunity;
  stage?: OpportunityStageHandle;
  now: Date;
}): ReactElement {
  const quotes = quotesOfOpportunity(crm, opportunity.id).map((quote) => quoteView(quote, now));
  const tasks = tasksOfOpportunity(crm, opportunity.id);
  return (
    <section
      className="opportunity-detail flex flex-col gap-4 rounded-panel border border-brand bg-brand-wash/30 p-4"
      data-section="negociacao-selecionada"
    >
      <h3 className="text-lg font-extrabold text-ink">{`Gerenciar: ${opportunity.title}`}</h3>
      <OpportunityStage opportunity={opportunity} stage={stage} />
      <h4 className="text-sm font-semibold text-ink">Orçamentos vinculados</h4>
      <RecordTable<ReturnType<typeof quoteView>>
        caption={`Orçamentos da negociação ${opportunity.title}`}
        rows={quotes}
        idOf={(item) => item.quote.id}
        emptyMessage="Nenhum orçamento vinculado a esta negociação."
        hideCaption
        animateRows
        columns={[
          {
            key: "number",
            label: "Proposta",
            render: (item) => <Link to={`/crm/orcamentos?quote=${encodeURIComponent(item.quote.id)}`}>{item.quote.number}</Link>
          },
          { key: "status", label: "Situação", render: (item) => QUOTE_STATUS_LABELS[item.status] },
          {
            key: "total",
            label: "Total",
            render: (item) =>
              item.totals.totalCents === null ? <span data-pending="true">sem frete cotado</span> : formatBrl(item.totals.totalCents)
          }
        ]}
      />
      <h4 className="text-sm font-semibold text-ink">Tarefas vinculadas</h4>
      <RecordTable<(typeof tasks)[number]>
        caption={`Tarefas da negociação ${opportunity.title}`}
        rows={tasks}
        idOf={(task) => task.id}
        emptyMessage="Nenhuma tarefa vinculada a esta negociação."
        hideCaption
        animateRows
        columns={[
          {
            key: "title",
            label: "Tarefa",
            render: (task) => <Link to={`/crm/tarefas?task=${encodeURIComponent(task.id)}`}>{task.title}</Link>
          },
          { key: "kind", label: "Tipo", render: (task) => TASK_KIND_LABELS[task.kind] },
          { key: "status", label: "Situação", render: (task) => TASK_STATUS_LABELS[task.status] ?? task.status },
          { key: "dueAt", label: "Prazo", render: (task) => formatDateTime(task.dueAt) }
        ]}
      />
    </section>
  );
}

export function PipelineView({
  crm,
  now = new Date(),
  stageFilter = null,
  ownerFilter = null,
  opportunityId = null,
  query = null,
  stage,
  onMove,
  initialMode = "quadro"
}: PipelineViewProps & { onMove?: (opportunityId: string, to: LeadStage) => void; initialMode?: "quadro" | "lista" }): ReactElement {
  const [mode, setMode] = useState<"quadro" | "lista">(initialMode);
  if (isCrmSourceAwaitingContract(crm, "opportunities")) {
    return (
      <div className="page page--pipeline flex flex-col gap-6" data-page="pipeline">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Pipeline comercial</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Pipeline ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/crm/opportunities não respondeu neste ambiente. Um funil vazio aqui diria que não há nenhuma negociação em andamento na operação."
        />
      </div>
    );
  }
  const columns = pipelineColumns(crm);
  const scoped = crm.opportunities.filter(
    (opportunity) =>
      (stageFilter === null || opportunity.stage === stageFilter) &&
      (ownerFilter === null || opportunity.ownerProfileId === ownerFilter)
  );
  // Busca textual: a relevância é a de `packages/admin-ops`
  // (`rankSearchResults`), a MESMA da busca universal do command center — um
  // segundo algoritmo ordenaria o mesmo termo de dois jeitos no mesmo produto.
  const search = query && query.trim() ? searchCrm(query, crm) : null;
  const ranked = search && !search.ignored ? rankedCrmIds(query ?? "", crm, "opportunity") : null;
  const filtered = ranked
    ? ranked.map((id) => scoped.find((opportunity) => opportunity.id === id)).filter((opportunity): opportunity is CrmOpportunity => opportunity !== undefined)
    : scoped;
  // Negociação exata pedida pelo drill-down do 360 OU selecionada na própria
  // tabela desta tela (Fase 9) — resolvida no dataset INTEIRO, não só no
  // recorte filtrado: selecionar e depois filtrar por outro estágio não deve
  // fazer o painel de detalhe sumir sem explicação.
  const selected = opportunityId === null ? null : (crm.opportunities.find((item) => item.id === opportunityId) ?? null);
  const tableContext = { stageFilter, ownerFilter, query };
  const open = crm.opportunities.filter((item) => item.stage !== "ganho" && item.stage !== "perdido");
  const openCount = open.length;
  const openValue = open.reduce((sum, item) => sum + (item.valueCents ?? 0), 0);
  const won = crm.opportunities.filter((item) => item.stage === "ganho");
  const wonCount = won.length;
  const wonValue = won.reduce((sum, item) => sum + (item.valueCents ?? 0), 0);
  const lostCount = crm.opportunities.filter((item) => item.stage === "perdido").length;
  return (
    <div
      className="page page--pipeline flex flex-col gap-6"
      data-page="pipeline"
      {...(opportunityId ? { "data-selected-opportunity": opportunityId } : {})}
    >
      <header className="drilldown-header flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Pipeline comercial</h2>
          <p className="drilldown-header__origin mt-1 text-base text-muted" data-pipeline-scope={stageFilter ?? "all"}>
            {stageFilter
              ? `${filtered.length} negociação(ões) no estágio "${LEAD_STAGE_LABELS[stageFilter]}".`
              : `${crm.opportunities.length} negociação(ões) no funil.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {stageFilter || ownerFilter || query ? (
            <Link to="/crm/pipeline" className="drilldown-header__back text-sm font-bold text-brand-text underline">
              Ver o funil completo
            </Link>
          ) : null}
          <Segmented<"quadro" | "lista">
            label="Modo de visualização"
            value={mode}
            onChange={setMode}
            options={[
              { value: "quadro", label: "Quadro" },
              { value: "lista", label: "Lista" }
            ]}
          />
        </div>
      </header>
      <dl aria-label="Resumo do funil" className="m-0 flex flex-wrap gap-x-10 gap-y-2 rounded-panel border border-border bg-surface px-5 py-3 shadow-s1">
        <div className="flex items-baseline gap-2" data-kpi="pipeline-aberto">
          <dt className="text-sm font-extrabold text-muted">Em aberto</dt>
          <dd className="m-0 text-xl font-black tabular-nums text-ink">{formatBrl(openValue)}</dd>
          <span className="text-xs text-muted">{`${openCount} negociação(ões)`}</span>
        </div>
        <div className="flex items-baseline gap-2" data-kpi="pipeline-ganhas">
          <dt className="text-sm font-extrabold text-muted">Ganhas</dt>
          <dd className="m-0 text-xl font-black tabular-nums text-success">{formatBrl(wonValue)}</dd>
          <span className="text-xs text-muted">{`${wonCount} fechada(s)`}</span>
        </div>
        <div className="flex items-baseline gap-2" data-kpi="pipeline-perdidas">
          <dt className="text-sm font-extrabold text-muted">Perdidas</dt>
          <dd className="m-0 text-xl font-black tabular-nums text-ink">{lostCount}</dd>
        </div>
      </dl>
      {ownerFilter ? (
        <p className="page__note text-sm text-muted" data-owner-scope={ownerFilter}>
          {`Recorte do responsável ${ownerFilter}.`}
        </p>
      ) : null}
      {search ? (
        <p className="page__note text-sm text-muted" data-pipeline-query={query} data-pipeline-query-ignored={search.ignored ?? "no"}>
          {search.ignored === "query_too_short"
            ? `Busca "${query}" ignorada: termo muito curto para pontuar relevância. A lista abaixo é o recorte completo, não um resultado de busca.`
            : `${filtered.length} negociação(ões) relevantes para "${query}", em ordem de relevância.`}
        </p>
      ) : null}
      {opportunityId ? (
        selected ? (
          <OpportunityDetail crm={crm} opportunity={selected} stage={stage} now={now} />
        ) : (
          <StatusPanel
            kind="empty"
            title={`A negociação ${opportunityId} não está neste recorte`}
            detail="O link pediu uma negociação que não existe (ou foi removida). Nada abaixo está destacado."
          />
        )
      ) : null}
      <div hidden={mode !== "quadro"} data-pipeline-mode="quadro">
        <PipelineBoard crm={crm} columns={columns} context={tableContext} now={now} selectedId={selected?.id ?? null} onMove={onMove} />
      </div>
      <div hidden={mode !== "lista"} data-pipeline-mode="lista" className="flex flex-col gap-4">
        <ColumnSummary columns={columns} />
        <OpportunityTable
          opportunities={filtered}
          selectedId={selected?.id ?? null}
          context={tableContext}
          caption={stageFilter ? `Negociações no estágio ${LEAD_STAGE_LABELS[stageFilter]}` : "Todas as negociações do funil"}
        />
      </div>
      <p className="page__note m-0 text-xs text-muted">
        Negociação ganha exige o pedido que a concretizou e negociação perdida exige motivo — o funil não aceita
        resultado sem a prova dele. Estágio é histórico auditável (crm_opportunity_events), não um campo sobrescrito.
        Arraste um cartão para avançar de estágio; ganho e perdido pedem a prova e abrem a negociação.
      </p>
    </div>
  );
}

export function Pipeline(): ReactElement {
  const [searchParams] = useSearchParams();
  const rawStage = searchParams.get("stage");
  const stageFilter = isLeadStage(rawStage) ? rawStage : null;
  const ownerFilter = searchParams.get("owner");
  const opportunityId = searchParams.get("opportunity");
  const query = searchParams.get("q");
  const mutation = useOpportunityStage();
  // Mesmo padrão de `Crm360.ts`: a view recebe o caminho de gravação por
  // prop, então o teste exercita o MESMO submit sem navegador. O ator não
  // viaja aqui — é derivado da sessão no servidor.
  const stage: OpportunityStageHandle = {
    submit: (input) => {
      if (!opportunityId) return;
      mutation.mutate({ opportunityId, input });
    },
    pending: mutation.isPending,
    error: mutation.error ?? null,
    savedStage: mutation.data?.stage ?? null
  };
  return (
    <CrmBoundary>
      {(crm: CrmDataset) => (
        <PipelineView
          crm={crm}
          stageFilter={stageFilter}
          ownerFilter={ownerFilter}
          opportunityId={opportunityId}
          query={query}
          stage={stage}
          onMove={(id, to) => mutation.mutate({ opportunityId: id, input: { to } })}
        />
      )}
    </CrmBoundary>
  );
}
